import { select, Store } from '@ngneat/elf';
import { Injectable } from '@angular/core';
import { ParamGeneralFieldProps, paramGeneralFieldInitialState } from './param-general-field.store';
import { BaseStoreRepository } from '../../base-store.repository';
import { ParamGeneralField, ParamGeneralStatus, ParamGeneralType } from '@/types';
import { SelectOption } from '@/components/form-builder/form-builder.types';

interface SystemType {
  value: string;
  label: string;
}

type SystemSubType = Record<string, SystemType[]>;

export const systemTypeOptions: SelectOption[] = [
  { code: 'ppm', name: 'PPM' },
  { code: 'ac', name: 'AC' },
  { code: 'realisation', name: 'Réalisation' },
  { code: 'ao', name: 'AO' },
  { code: 'dac', name: 'DAC' },
];

export const systemSubTypes: SystemSubType = {
  ao: [
    { value: 'ref', label: 'Référence AO' },
    { value: 'nom', label: 'Nom AO' },
    { value: 'description', label: 'Description AO' },
  ],
  dac: [
    { value: 'ref', label: 'Référence DAC' },
    { value: 'payant', label: 'Payant' },
    { value: 'montant', label: 'Montant' },
    { value: 'first_name_laste_name_responable_dac', label: 'Nom et Prénom Responsable' },
    { value: 'tel_responable_dac', label: 'Tél Responsable' },
    { value: 'email_responable_dac', label: 'Email Responsable' },
    { value: 'adresse_responable_dac', label: 'Adresse Responsable' },
    { value: 'condition_participation', label: 'Conditions de participation' },
    { value: 'date_limite_soumission', label: 'Date limite de soumission' },
    { value: 'heure_limite_soumission', label: 'Heure limite de soumission' },
    { value: 'date_limite_reception_demande', label: 'Date limite réception des demandes' },
    { value: 'date_ouverture', label: "Date d'ouverture" },
    { value: 'heure_ouverture', label: "Heure d'ouverture" },
    { value: 'date_debut_depot', label: "Date de début de depot" },
    { value: 'date_creation', label: 'Date de création' },
  ],
  ppm: [
    { value: 'ref', label: 'Référence PPM' },
    { value: 'date_publication', label: 'Date publication PPM' },
  ],
  ac: [
    { value: 'nom', label: 'Nom AC' },
    { value: 'adresse', label: 'Adresse AC' },
    { value: 'email', label: 'Email AC' },
    { value: 'tel1', label: 'Tél 1 AC' },
    { value: 'tel2', label: 'Tél 2 AC' },
  ],
  realisation: [
    { value: 'realisation_envisagees', label: 'Objet Réalisation' },
    { value: 'beneficiaire', label: 'Beneficiaire' },
    { value: 'type_marcher', label: 'Type de marché' },
    { value: 'budget_estime', label: 'Budget estime' },
    { value: 'source_financement', label: 'Source(s) de financement' },
    { value: 'mode_passation', label: 'Mode de Passation' },
    { value: 'portee', label: 'Portée' },
    { value: 'nom_lot', label: 'Lots' },
    { value: 'date_prev_lps', label: 'Date prévue de lancement' },
    { value: 'date_prev_ac', label: "Date prévue d'attribution du contrat" },
    { value: 'date_prev_dp', label: 'Date prévue de démarrage des prestations' },
    { value: 'date_prev_ap', label: "Date prévue d'achèvement des prestations " },
  ],
};

export type ParamGeneralFieldStore = Store<
  {
    name: string;
    state: ParamGeneralFieldProps;
    config: undefined;
  },
  ParamGeneralFieldProps
>;

@Injectable({ providedIn: 'root' })
export class ParamGeneralFieldRepositoryFactory {
  static create(store: ParamGeneralFieldStore) {
    return new ParamGeneralFieldRepository(store);
  }
}

export class ParamGeneralFieldRepository extends BaseStoreRepository<ParamGeneralFieldProps> {
  constructor(store: ParamGeneralFieldStore) {
    super(store.pipe(select(state => state)), store, { ...paramGeneralFieldInitialState });
  }

  setFromResponse(field: ParamGeneralField): void {
    this.set('dto', {
      id: field.id,
      paramGeneralName: field.paramGeneralName || '',
      paramGeneralLabel: field.paramGeneralLabel || '',
      paramGeneralDescription: field.paramGeneralDescription || '',
      paramGeneralType: field.paramGeneralType,
      paramGeneralOrder: field.paramGeneralOrder || 0,
      paramGeneralSource: field.paramGeneralSource || '',
      paramGeneralSourceField: field.paramGeneralSourceField || '',
      paramGeneralStatus: field.paramGeneralStatus || ParamGeneralStatus.ACTIF,
    });
    if (field.paramGeneralType) {
      this.set('typeSelected', { name: field.paramGeneralType, code: field.paramGeneralType });
    }
    if (field.paramGeneralStatus) {
      this.set('statusSelected', {
        name: field.paramGeneralStatus === ParamGeneralStatus.ACTIF ? 'Actif' : 'Inactif',
        code: field.paramGeneralStatus,
      });
    }
    // Set source fields if type is SYSTEM
    if (field.paramGeneralType === ParamGeneralType.SYSTEM && field.paramGeneralSource) {
      const sourceOption = systemTypeOptions.find(opt => opt.code === field.paramGeneralSource);
      if (sourceOption) {
        this.set('sourceSelected', sourceOption);
      }
      if (field.paramGeneralSourceField) {
        const subTypes = systemSubTypes[field.paramGeneralSource] || [];
        const sourceFieldOption = subTypes.find(st => st.value === field.paramGeneralSourceField);
        if (sourceFieldOption) {
          this.set('sourceFieldSelected', { name: sourceFieldOption.label, code: sourceFieldOption.value });
        }
      }
    }
  }
}
